// The Management Client's own session expiry (documentation/entra.md "Session lifetimes"). The
// server decides validity from auth_sessions.expires_at; this only signs the page out AT that
// moment instead of at the next request, and keeps the moment current when the platform policy
// changes (a reduction shortens, an increase extends a live session). GET /api/auth/me states the
// expiry and the server's clock, so the local deadline does not depend on the browser's clock.

import type { Me } from './identity';

/** How often the identity is re-read to pick up a policy change (and an ended session). */
export const SESSION_REFRESH_MS = 60_000;
/** setTimeout cannot wait longer than this (about 24.8 days); longer waits are re-armed. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * Milliseconds from the moment `me` was received until the session ends (0 = already ended), or
 * null when the identity carries no session expiry (a legacy JWT or a DEV_MODE identity).
 */
export function msUntilExpiry(me: Pick<Me, 'session_expires_at' | 'server_time'> | null): number | null {
  if (!me?.session_expires_at || !me.server_time) return null;
  const expires = Date.parse(me.session_expires_at);
  const server = Date.parse(me.server_time);
  if (!Number.isFinite(expires) || !Number.isFinite(server)) return null;
  return Math.max(0, expires - server);
}

export interface ExpiryClock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const browserClock: ExpiryClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (h) => window.clearTimeout(h as number),
};

/**
 * Watches the session of `me`: calls onExpired once at its expiry, or as soon as a re-read
 * (`reload`, every SESSION_REFRESH_MS) answers that there is no session any more. Every re-read
 * moves the deadline to the server's current answer. Returns the stop function.
 */
export function watchSessionExpiry(
  me: Me,
  reload: () => Promise<Me | null>,
  onExpired: () => void,
  onIdentity: (me: Me) => void = () => undefined,
  clock: ExpiryClock = browserClock,
): () => void {
  let stopped = false;
  let deadline: number | null = null;
  let expiryTimer: unknown = null;
  let refreshTimer: unknown = null;

  const expire = () => {
    if (stopped) return;
    stop();
    onExpired();
  };
  const arm = () => {
    if (expiryTimer !== null) clock.clearTimeout(expiryTimer);
    expiryTimer = null;
    if (deadline === null) return;
    const wait = deadline - clock.now();
    if (wait <= 0) {
      expire();
      return;
    }
    expiryTimer = clock.setTimeout(arm, Math.min(wait, MAX_TIMER_MS));
  };
  const apply = (m: Me) => {
    const ms = msUntilExpiry(m);
    deadline = ms === null ? null : clock.now() + ms;
    arm();
  };
  const refresh = async () => {
    refreshTimer = null;
    if (stopped) return;
    try {
      const m = await reload();
      if (stopped) return;
      if (!m) {
        expire();
        return;
      }
      onIdentity(m);
      apply(m);
    } catch {
      // Unreachable server: the deadline stands; try again at the next interval.
    }
    if (!stopped) refreshTimer = clock.setTimeout(refresh, SESSION_REFRESH_MS);
  };
  const stop = () => {
    stopped = true;
    if (expiryTimer !== null) clock.clearTimeout(expiryTimer);
    if (refreshTimer !== null) clock.clearTimeout(refreshTimer);
    expiryTimer = refreshTimer = null;
  };

  apply(me);
  if (!stopped) refreshTimer = clock.setTimeout(refresh, SESSION_REFRESH_MS);
  return stop;
}
